import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { apiService, API_CONFIG } from '../../../api/apiClient';
import InfoBox from '../InfoBox';
import { useChartTokens } from '../../../theme/charts';

export default function AnalyticsTab() {
  const { t } = useTranslation();
  const [range, setRange] = useState({ from: '', to: '' });

  const queryParams = useCallback(() => {
    const p = {};
    if (range.from) p.from = new Date(range.from).toISOString();
    if (range.to) p.to = new Date(range.to).toISOString();
    return p;
  }, [range]);

  return (
    <div>
      <InfoBox tabKey="analytics" />

      <div className="flex flex-wrap gap-3 items-end mb-6 pb-4 border-b border-border-subtle">
        <RangeInput label={t('traceability:filters.from')} value={range.from}
                    onChange={(v) => setRange({ ...range, from: v })} />
        <RangeInput label={t('traceability:filters.to')} value={range.to}
                    onChange={(v) => setRange({ ...range, to: v })} />
        <button onClick={() => setRange({ from: '', to: '' })}
                className="text-label tracking-widest text-ink-muted hover:text-accent-text px-3 py-1">
          {t('traceability:filters.clear')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard metric="events_over_time" params={queryParams()}>
          {(data) => <EventsOverTime data={data} />}
        </ChartCard>
        <ChartCard metric="latency_by_phase" params={queryParams()}>
          {(data) => <LatencyByPhase data={data} />}
        </ChartCard>
        <ChartCard metric="error_rate_by_type" params={queryParams()}>
          {(data) => <ErrorRatePie data={data} />}
        </ChartCard>
        <ChartCard metric="tokens_over_time" params={queryParams()}>
          {(data) => <TokensOverTime data={data} />}
        </ChartCard>
        <ChartCard metric="confidence_distribution" params={queryParams()}>
          {(data) => <ConfidenceHistogram data={data} />}
        </ChartCard>
        <ChartCard metric="top_labels" params={queryParams()}>
          {(data) => <HBar data={data} xKey="n" yKey="label" />}
        </ChartCard>
        <ChartCard metric="top_rel_types" params={queryParams()}>
          {(data) => <HBar data={data} xKey="n" yKey="rel_type" />}
        </ChartCard>
        <ChartCard metric="ingestion_duration" params={queryParams()}>
          {(data) => <IngestionScatter data={data} />}
        </ChartCard>
        <ChartCard metric="rows_returned_histogram" params={queryParams()}>
          {(data) => <RowsHistogram data={data} />}
        </ChartCard>
        <ChartCard metric="activity_heatmap" params={queryParams()}>
          {(data) => <Heatmap data={data} />}
        </ChartCard>
      </div>
    </div>
  );
}

function RangeInput({ label, value, onChange }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-label tracking-widest text-ink-muted uppercase">{label}</span>
      <input type="datetime-local" value={value} onChange={(e) => onChange(e.target.value)}
             className="bg-surface border border-border-strong rounded px-2 py-1 text-sm text-ink" />
    </label>
  );
}

function isEmptySeries(data) {
  const s = data?.series;
  if (s == null) return true;
  if (Array.isArray(s)) return s.length === 0;
  if (typeof s === 'object') return Object.keys(s).length === 0;
  return true;
}

function ChartCard({ metric, params, children }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService.getAuditChart(metric, params)
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => { if (!cancelled) setData({ series: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [metric, JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCsv = useCallback(() => {
    const qp = new URLSearchParams({ metric, format: 'csv', ...params }).toString();
    const url = `${API_CONFIG.baseURL}${API_CONFIG.endpoints.audit.charts}?${qp}`;
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    a.click();
  }, [metric, params]);

  const prefix = `traceability:charts.${metric}`;
  const empty = !loading && isEmptySeries(data);

  return (
    <section className="rounded-lg border border-border-subtle bg-surface-raised/40 p-4">
      <header className="flex items-start justify-between mb-3 gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">{t(`${prefix}.title`)}</h3>
          <p className="text-xs text-ink-muted mt-1">{t(`${prefix}.what`)}</p>
        </div>
        <button onClick={downloadCsv}
                className="text-label tracking-widest text-ink-muted hover:text-accent-text border border-border-strong rounded px-2 py-1 shrink-0">
          CSV
        </button>
      </header>

      <details className="mb-3 text-xs text-ink-secondary">
        <summary className="cursor-pointer text-ink-muted">ⓘ {t('traceability:infobox.toggle')}</summary>
        <div className="mt-2 space-y-2">
          <div><span className="text-ink-muted uppercase tracking-widest text-[10px]">Cálculo:</span>
            <div className="font-mono text-[11px] bg-surface px-2 py-1 rounded mt-1">{t(`${prefix}.how`)}</div></div>
          <div><span className="text-ink-muted uppercase tracking-widest text-[10px]">Lectura:</span> {t(`${prefix}.read`)}</div>
        </div>
      </details>

      <div style={{ width: '100%', height: 260 }}>
        {loading && <div className="text-ink-muted text-sm">···</div>}
        {empty && !loading && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="text-label tracking-widest text-ink-muted uppercase mb-2">
              {t('traceability:charts.empty.title')}
            </div>
            <p className="text-xs text-ink-secondary max-w-xs leading-relaxed">
              {t(`${prefix}.hint`, { defaultValue: t('traceability:charts.empty.genericHint') })}
            </p>
          </div>
        )}
        {!loading && !empty && data && children(data)}
      </div>
    </section>
  );
}

function EventsOverTime({ data }) {
  const ct = useChartTokens();
  const { t } = useTranslation();
  const series = data?.series || {};
  const buckets = new Set();
  Object.values(series).forEach((rows) => rows.forEach((r) => buckets.add(r.bucket)));
  const sorted = Array.from(buckets).sort();
  const merged = sorted.map((b) => {
    const out = { bucket: b };
    for (const [type, rows] of Object.entries(series)) {
      const hit = rows.find((r) => r.bucket === b);
      out[type] = hit ? hit.n : 0;
    }
    return out;
  });
  const types = Object.keys(series);
  const totals = Object.fromEntries(
    types.map((type) => [type, (series[type] || []).reduce((acc, r) => acc + (r.n || 0), 0)])
  );
  const prefix = 'traceability:charts.events_over_time.types';
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0">
        <ResponsiveContainer>
          <LineChart data={merged}>
            <CartesianGrid stroke={ct.gridStroke} strokeDasharray="3 3" />
            <XAxis dataKey="bucket" tick={{ fill: ct.axisFill, fontSize: 10 }} />
            <YAxis tick={{ fill: ct.axisFill, fontSize: 10 }} />
            <Tooltip
              contentStyle={ct.tooltipStyle}
              itemStyle={ct.tooltipItemStyle}
              labelStyle={ct.tooltipLabelStyle}
              formatter={(value, name) => [value, t(`${prefix}.${name}.label`, { defaultValue: name })]}
            />
            {types.map((type, i) => (
              <Line key={type} type="monotone" dataKey={type}
                    stroke={ct.palette[i % ct.palette.length]} strokeWidth={1.5} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] leading-tight">
        {types.map((type, i) => {
          const desc = t(`${prefix}.${type}.desc`, { defaultValue: '' });
          return (
            <div key={type} className="flex items-center gap-2 min-w-0" title={desc}>
              <span
                className="inline-block h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: ct.palette[i % ct.palette.length] }}
              />
              <span className="text-ink font-medium shrink-0">
                {t(`${prefix}.${type}.label`, { defaultValue: type })}
              </span>
              <span className="text-ink-muted font-mono shrink-0">{totals[type]}</span>
              {desc && <span className="text-ink-muted truncate">— {desc}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LatencyByPhase({ data }) {
  const ct = useChartTokens();
  const rows = (data?.series || []).map((r) => ({
    phase: r.phase, p50: r.p50 || 0, p95: r.p95 || 0, p99: r.p99 || 0,
  }));
  return (
    <ResponsiveContainer>
      <BarChart data={rows}>
        <CartesianGrid stroke={ct.gridStroke} strokeDasharray="3 3" />
        <XAxis dataKey="phase" tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <YAxis tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <Tooltip contentStyle={ct.tooltipStyle} itemStyle={ct.tooltipItemStyle} labelStyle={ct.tooltipLabelStyle} />
        <Legend wrapperStyle={ct.legendStyle} />
        <Bar dataKey="p50" fill={ct.palette[0]} />
        <Bar dataKey="p95" fill={ct.palette[2]} />
        <Bar dataKey="p99" fill={ct.palette[3]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ErrorRatePie({ data }) {
  const ct = useChartTokens();
  const rows = data?.series || [];
  const totalErrors = rows.reduce((s, r) => s + (r.error || 0), 0);
  const pie = rows.map((r) => ({ name: r.event_type, value: r.error || 0 }))
                  .filter((r) => r.value > 0);
  if (!pie.length) {
    return <div className="text-ink-muted text-sm flex items-center justify-center h-full">—</div>;
  }
  return (
    <ResponsiveContainer>
      <PieChart>
        <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
             label={(e) => `${e.name} ${Math.round(e.value/totalErrors*100)}%`}>
          {pie.map((_, i) => <Cell key={i} fill={ct.palette[i % ct.palette.length]} />)}
        </Pie>
        <Tooltip contentStyle={ct.tooltipStyle} itemStyle={ct.tooltipItemStyle} labelStyle={ct.tooltipLabelStyle} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function TokensOverTime({ data }) {
  const ct = useChartTokens();
  const rows = data?.series || [];
  const days = Array.from(new Set(rows.map((r) => r.day))).sort();
  const phases = Array.from(new Set(rows.map((r) => r.phase)));
  const merged = days.map((d) => {
    const out = { day: d };
    for (const ph of phases) {
      const hit = rows.find((r) => r.day === d && r.phase === ph);
      out[ph] = hit ? hit.tokens : 0;
    }
    return out;
  });
  return (
    <ResponsiveContainer>
      <AreaChart data={merged}>
        <CartesianGrid stroke={ct.gridStroke} strokeDasharray="3 3" />
        <XAxis dataKey="day" tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <YAxis tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <Tooltip contentStyle={ct.tooltipStyle} itemStyle={ct.tooltipItemStyle} labelStyle={ct.tooltipLabelStyle} />
        <Legend wrapperStyle={ct.legendStyle} />
        {phases.map((ph, i) => (
          <Area key={ph} type="monotone" dataKey={ph} stackId="1"
                stroke={ct.palette[i % ct.palette.length]} fill={ct.palette[i % ct.palette.length]} fillOpacity={0.5} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function ConfidenceHistogram({ data }) {
  const ct = useChartTokens();
  const bins = Array.from({ length: 11 }, (_, i) => ({ bin: i, n: 0 }));
  for (const r of (data?.series || [])) {
    if (r.bin >= 0 && r.bin <= 10) bins[r.bin].n = r.n;
  }
  return (
    <ResponsiveContainer>
      <BarChart data={bins}>
        <CartesianGrid stroke={ct.gridStroke} strokeDasharray="3 3" />
        <XAxis dataKey="bin" tickFormatter={(v) => (v / 10).toFixed(1)}
               tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <YAxis tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <Tooltip contentStyle={ct.tooltipStyle} itemStyle={ct.tooltipItemStyle} labelStyle={ct.tooltipLabelStyle} />
        <Bar dataKey="n" fill={ct.palette[0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function HBar({ data, xKey, yKey }) {
  const ct = useChartTokens();
  const rows = (data?.series || []).slice().reverse();
  return (
    <ResponsiveContainer>
      <BarChart data={rows} layout="vertical">
        <CartesianGrid stroke={ct.gridStroke} strokeDasharray="3 3" />
        <XAxis type="number" tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <YAxis type="category" dataKey={yKey} width={120} tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <Tooltip contentStyle={ct.tooltipStyle} itemStyle={ct.tooltipItemStyle} labelStyle={ct.tooltipLabelStyle} />
        <Bar dataKey={xKey} fill={ct.palette[2]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function IngestionScatter({ data }) {
  const ct = useChartTokens();
  const rows = (data?.series || []).map((r) => {
    const s = r.started_at ? new Date(r.started_at).getTime() : 0;
    const e = r.ended_at ? new Date(r.ended_at).getTime() : s;
    return { x: s, y: Math.max(0, e - s), status: r.status };
  });
  const ok = rows.filter((r) => r.status !== 'error');
  const err = rows.filter((r) => r.status === 'error');
  return (
    <ResponsiveContainer>
      <ScatterChart>
        <CartesianGrid stroke={ct.gridStroke} strokeDasharray="3 3" />
        <XAxis type="number" dataKey="x" name="started"
               tickFormatter={(v) => v ? new Date(v).toLocaleDateString() : ''}
               tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <YAxis type="number" dataKey="y" name="duration_ms"
               tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <Tooltip contentStyle={ct.tooltipStyle} itemStyle={ct.tooltipItemStyle} labelStyle={ct.tooltipLabelStyle} />
        <Legend wrapperStyle={ct.legendStyle} />
        <Scatter name="ok" data={ok} fill={ct.palette[1]} />
        <Scatter name="error" data={err} fill={ct.palette[3]} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function RowsHistogram({ data }) {
  const ct = useChartTokens();
  const rows = (data?.series || []).map((r) => ({
    bucket: r.bucket < 0 ? '0' : `10^${r.bucket}`,
    n: r.n,
  }));
  return (
    <ResponsiveContainer>
      <BarChart data={rows}>
        <CartesianGrid stroke={ct.gridStroke} strokeDasharray="3 3" />
        <XAxis dataKey="bucket" tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <YAxis tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <Tooltip contentStyle={ct.tooltipStyle} itemStyle={ct.tooltipItemStyle} labelStyle={ct.tooltipLabelStyle} />
        <Bar dataKey="n" fill={ct.palette[4]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Heatmap({ data }) {
  const ct = useChartTokens();
  const rows = (data?.series || []).map((r) => ({
    x: r.hour, y: r.dow, z: r.n,
  }));
  return (
    <ResponsiveContainer>
      <ScatterChart>
        <CartesianGrid stroke={ct.gridStroke} strokeDasharray="3 3" />
        <XAxis type="number" dataKey="x" name="hour" domain={[0, 23]}
               tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <YAxis type="number" dataKey="y" name="dow" domain={[1, 7]}
               tick={{ fill: ct.axisFill, fontSize: 10 }} />
        <Tooltip contentStyle={ct.tooltipStyle} itemStyle={ct.tooltipItemStyle} labelStyle={ct.tooltipLabelStyle} />
        <Scatter data={rows} fill={ct.palette[0]}
                 shape={(p) => {
                   const r = Math.max(3, Math.sqrt(p.payload.z || 1) * 2);
                   return <circle cx={p.cx} cy={p.cy} r={r} fill={ct.palette[0]} opacity={0.7} />;
                 }} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}
